import { useEffect, useMemo, useState } from "react"
import { StyledTable } from "baseui/table-grid"
import { colors } from "../../Styles"
import { LoadingSpinner } from ".."
import { TableStyles_Types, createTableStyle, SelectFieldOption_Values } from "../../Definitions"
import { Row, RowData, Table } from "@tanstack/react-table"
import { TableContent } from "./_Tables/getTableContent"
import { convertPxValueToNumber, getWidthIfPinned } from "./_Tables/getTableStyles"
import { HEADER_OR_FOOTER_KEY, HeaderAndFooter } from "./_Tables/getTableHeader"

interface TableListProps {
  tableType?: TableStyles_Types,
  table: Table<any>,
  emptyText: string,
  renderFooter?: boolean
  tableToggled?: number | boolean
  shouldAlwaysRerender?: boolean
  loading?: boolean;
  highlightedRowId?: number;
  minRows?: number;
}

export interface IdentifiableRow {
  id: number
}

export interface TableList_PropHandles {
  changed: {row:number,col:number, value:any}[],
}

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: string | number | boolean | SelectFieldOption_Values | undefined, row: Row<TData>) => void;
    deleteRow: (rowId: number) => void;
    allowDeleteRowCheck?: (row: Row<TData>) => boolean;
    checkRow?: (rowId: number) => void;
  }
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    width?: string;
    forceRerenderOnTableEdit?: true;
  }
}

const ROW_HEIGHT = 40;
export const ReactiveTable = ({ tableType, highlightedRowId, table, emptyText, renderFooter=true, tableToggled, shouldAlwaysRerender, loading, minRows }: TableListProps) => {
  const { tableStyle, bodyCellStyle } = useMemo(() => createTableStyle(tableType),[tableType]);
 
  const headers = table.getHeaderGroups().flatMap((headerGroups) => (headerGroups.headers));
  const rows = table.getRowModel().rows;
  const {columnWidth, columnWidthNumber} = headers.reduce((accumulator, header) => {
    const value = getWidthIfPinned(header.column.getIsPinned(), header.column.columnDef.meta?.width)
    accumulator.columnWidth.push(value);
    accumulator.columnWidthNumber.push(convertPxValueToNumber(value));
    return accumulator;
  }, {columnWidth: [] as string[], columnWidthNumber: [] as number[]});

  const { headCellStyle } = createTableStyle();
  const headerHeight = headCellStyle.height
    ? parseInt(headCellStyle.height.toString().replace("px", ""), 10)
    : 30;
  const highlightIndex = rows.findIndex(r => r.original.id === highlightedRowId);

  const gridTemplateColumns = columnWidth.reduce((acc, val) => {
    return (acc + val);
  }, "")

  return (<>
    <StyledTable style={{...tableStyle, position: "relative"}} role="grid" $gridTemplateColumns={gridTemplateColumns} >
      <div role="columnheader" key="header" style={{ display: "contents" }}>
        <HeaderAndFooter loading={loading} headers={headers} headerOrFooterKey={HEADER_OR_FOOTER_KEY.header} columnWidth={columnWidthNumber} tableStyle={tableType ? tableType : TableStyles_Types.primaryTable} tableToggle={tableToggled}/>
      </div>

      {/* {(highlightedRowId !== undefined && highlightIndex >= 0) && (
        <TableHighlight index={highlightIndex} rowHeight={rowHeight} headerHeight={headerHeight} />
      )} */}

      {!loading && (
        <div key="tableBody" role="tablist" style={{ display: "contents", overflow: "scroll" }}>
          <TableContent minRows={minRows} rows={rows} highlightedRowId={highlightedRowId} emptyText={emptyText} bodyCellStyle={bodyCellStyle} tableType={tableType ? tableType : TableStyles_Types.primaryTable} columnWidths={columnWidthNumber} tableToggled={tableToggled} shouldAlwaysRerender={shouldAlwaysRerender}/>
        </div>
      )}

      {loading && (<div style={{width: "100%", height: "100%", minHeight: minRows ? `${ROW_HEIGHT*minRows}px`: "250px", alignContent: "center", justifyContent: "center", gridColumn: "1/-1"}}><LoadingSpinner /></div> )}
      
      {renderFooter && (
        <div role="rowheader" key="footer" style={{ display: "contents" }}>
          <HeaderAndFooter loading={loading} headers={headers} headerOrFooterKey={HEADER_OR_FOOTER_KEY.footer} columnWidth={columnWidthNumber} tableStyle={tableType ? tableType : TableStyles_Types.primaryTable} tableToggle={tableToggled} />
        </div>
      )}
    </StyledTable>

    {/* {(highlightedRowId !== undefined && highlightIndex >= 0) && (
      <TableHighlight index={highlightIndex} rowHeight={rowHeight} headerHeight={headerHeight} />
    )} */}

  </>);
}

const TableHighlight = ({index, rowHeight, headerHeight}: {index: number; rowHeight: number; headerHeight: number}) => {
  const [y, setY] = useState(index * rowHeight + headerHeight);

  useEffect(() => {
    setY(index * rowHeight + headerHeight);
  }, [index, rowHeight, headerHeight]);

  return (
    <div
      style={{
        position: "absolute",
        top: "0px",
        left: "10px",
        right: "10px",
        height: rowHeight,
        border: `3px solid ${colors.whitePrimary}`,
        zIndex: 9999,
        boxSizing: "border-box",
        transition: "transform 0.5s ease",
        transform: `translateY(${y}px)`,
        borderRadius: "10px",
        pointerEvents: "none",
      }}
    />
  );
};